// frontend/src/components/flags/FlagLeafControl.tsx
/**
 * One manifest leaf, one control.
 *
 * The mapping is data-driven: `type` picks the control, `role` refines it
 * (a caption is a text field, an rbac leaf is a chip group drawn from the
 * manifest's own roles_vocabulary — never a hard-coded role list, which is
 * precisely the mirroring this work exists to remove).
 *
 * The presentation is the portal's own design system, not MUI. The boolean
 * control is the very `Toggle` the hand-written catalogue above it uses, the
 * role chips carry the catalogue's role-chip voice, and every colour, size,
 * radius and font comes from `design-system/tokens`. An enable switch has to
 * look identical whether the portal's built-in list or the tenant's manifest
 * put it on the page; two renderings of one concept on one screen is the
 * defect this file exists to remove.
 */
import React, { useState } from 'react';
import styled, { css } from 'styled-components';
import { Toggle } from '../../design-system/components/Toggle';
import {
  spacing, typography, borderRadius, brandColors,
} from '../../design-system/tokens';
import { FlagLeaf, FlagNode } from '../../services/api';

interface Props {
  leaf: FlagLeaf;
  node: FlagNode;
  value: any;
  rolesVocabulary: string[];
  disabled?: boolean;
  onChange: (value: any) => void;
}

// =============================================================================
// STYLED COMPONENTS
// =============================================================================

/**
 * One ring for every focusable thing here. 2px of the brand teal clears 3:1
 * against every surface this tree draws on, in both themes, and nothing in
 * this file transitions `outline` — so it lands the instant the key does.
 */
const focusRing = css`
  &:focus-visible {
    outline: 2px solid ${props => props.theme?.colors?.accent?.primary || brandColors.silaTeal};
    outline-offset: 2px;
  }
`;

/** The catalogue's own field voice (see NumberInput in FeatureFlagsEditor). */
const fieldSurface = css`
  font-family: inherit;
  font-size: ${typography.fontSize.sm};
  color: ${props => props.theme?.colors?.text?.primary || '#ffffff'};
  background: ${props => props.theme?.colors?.background?.secondary || 'rgba(26, 26, 26, 0.5)'};
  border: 1px solid ${props => props.theme?.colors?.border?.primary || 'rgba(255, 255, 255, 0.1)'};
  border-radius: ${borderRadius.md};
  padding: ${spacing['1.5']} ${spacing['2']};
  max-width: 100%;

  &::placeholder {
    color: ${props => props.theme?.colors?.input?.placeholder || '#64748b'};
  }

  &:hover:not(:disabled) {
    border-color: ${props => props.theme?.colors?.accent?.primary || brandColors.silaTeal};
  }

  &:focus {
    outline: none;
    border-color: ${props => props.theme?.colors?.accent?.primary || brandColors.silaTeal};
  }

  ${focusRing}

  &:disabled {
    opacity: 0.5;
    cursor: not-allowed;
  }
`;

const LeafWrap = styled.div`
  display: flex;
  flex-direction: column;
  gap: ${spacing['1']};
  min-width: 0;
  max-width: 100%;
`;

/** Label before control, as every row in the catalogue reads. */
const LeafField = styled.label<{ $disabled: boolean }>`
  display: inline-flex;
  align-items: center;
  flex-wrap: wrap;
  gap: ${spacing['1']} ${spacing['2']};
  min-width: 0;
  max-width: 100%;
  cursor: ${props => (props.$disabled ? 'not-allowed' : 'pointer')};
`;

const LeafKey = styled.span`
  font-size: ${typography.fontSize.xs};
  font-weight: ${typography.fontWeight.medium};
  color: ${props => props.theme?.colors?.text?.secondary || '#94a3b8'};
  white-space: nowrap;
`;

const LeafNote = styled.span`
  font-size: ${typography.fontSize.xs};
  line-height: ${typography.lineHeight.normal};
  /* text.secondary, the app's supporting-text colour: tertiary at 11px on the
     panels this tree draws on is about 3:1, which is not a reading contrast. */
  color: ${props => props.theme?.colors?.text?.secondary || '#94a3b8'};
  max-width: 260px;
`;

/**
 * A disabled control is unreachable, not merely faded: the whole point of
 * gating is that a click here would save and do nothing.
 */
const ToggleWrap = styled.div<{ $disabled: boolean }>`
  display: inline-flex;
  ${props => props.$disabled && css`
    pointer-events: none;
  `}
`;

/**
 * The design-system Toggle, re-voiced rather than re-implemented: the label
 * moves to the left of the switch (the catalogue's reading order) and takes
 * the same quiet key styling as every other leaf label. The only <span> a
 * label-only Toggle renders is that label.
 */
const LeafToggle = styled(Toggle)`
  /* Doubled on purpose: Toggle is a plain component, so its own wrapper rule is
     injected after this one and would otherwise win the tie. */
  && {
    flex-direction: row-reverse;
    justify-content: flex-start;
    align-items: center;
    gap: ${spacing['2']};
  }

  && span {
    font-size: ${typography.fontSize.xs};
    font-weight: ${typography.fontWeight.medium};
    color: ${props => props.theme?.colors?.text?.secondary || '#94a3b8'};
    white-space: nowrap;
  }

  /* The shared Toggle puts keyboard focus on its switch and draws nothing for
     it. Ringing it here is cheaper, and safer, than editing a component eleven
     other screens render. */
  && [role='switch']:focus-visible {
    outline: 2px solid ${props => props.theme?.colors?.accent?.primary || brandColors.silaTeal};
    outline-offset: 2px;
  }
`;

const TextInput = styled.input`
  ${fieldSurface}
  width: 200px;
`;

const NumberInput = styled.input`
  ${fieldSurface}
  width: 110px;
  text-align: center;

  /* Hide spinner buttons, as the catalogue's own number field does */
  -moz-appearance: textfield;
  &::-webkit-outer-spin-button,
  &::-webkit-inner-spin-button {
    -webkit-appearance: none;
    margin: 0;
  }
`;

const ChipGroup = styled.div`
  display: inline-flex;
  align-items: center;
  flex-wrap: wrap;
  gap: ${spacing['1']};
  max-width: 100%;
`;

/** The catalogue's RoleCheckbox, with a real focusable input behind it. */
const Chip = styled.label<{ $checked: boolean; $disabled?: boolean }>`
  display: inline-flex;
  align-items: center;
  gap: ${spacing['1']};
  padding: ${spacing['1']} ${spacing['2']};
  font-size: ${typography.fontSize.xs};
  white-space: nowrap;
  color: ${props => (props.$checked
    ? props.theme?.colors?.text?.primary || '#ffffff'
    : props.theme?.colors?.text?.secondary || '#94a3b8')};
  background: ${props => (props.$checked
    ? `${props.theme?.colors?.accent?.primary || brandColors.silaTeal}20`
    : 'transparent')};
  border: 1px solid ${props => (props.$checked
    ? props.theme?.colors?.accent?.primary || brandColors.silaTeal
    : props.theme?.colors?.border?.secondary || 'rgba(255, 255, 255, 0.1)')};
  border-radius: ${borderRadius.sm};
  cursor: ${props => (props.$disabled ? 'not-allowed' : 'pointer')};
  opacity: ${props => (props.$disabled ? 0.5 : 1)};

  ${props => !props.$disabled && css`
    &:hover {
      background: ${props.$checked
        ? `${props.theme?.colors?.accent?.primary || brandColors.silaTeal}30`
        : props.theme?.colors?.background?.hover || 'rgba(255, 255, 255, 0.05)'};
    }

    &:active {
      background: ${`${props.theme?.colors?.accent?.primary || brandColors.silaTeal}30`};
    }
  `}

  &:has(input:focus-visible) {
    outline: 2px solid ${props => props.theme?.colors?.accent?.primary || brandColors.silaTeal};
    outline-offset: 2px;
  }
`;

/**
 * Clipped, not `display: none`: a hidden-by-display checkbox is neither
 * focusable nor reachable by a screen reader, which would make the chips
 * keyboard-dead. Same technique the design-system Toggle uses.
 */
const HiddenCheckbox = styled.input`
  position: absolute;
  width: 1px;
  height: 1px;
  padding: 0;
  margin: -1px;
  overflow: hidden;
  clip: rect(0, 0, 0, 0);
  white-space: nowrap;
  border: 0;
`;

const ChipRemove = styled.button`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  padding: 0;
  width: 14px;
  height: 14px;
  font-size: ${typography.fontSize.xs};
  line-height: 1;
  color: inherit;
  background: transparent;
  border: none;
  border-radius: ${borderRadius.full};
  cursor: pointer;

  &:hover {
    background: ${props => props.theme?.colors?.background?.hover || 'rgba(255, 255, 255, 0.05)'};
  }

  ${focusRing}

  &:disabled {
    opacity: 0.5;
    cursor: not-allowed;
  }
`;

const ChipInput = styled.input`
  ${fieldSurface}
  width: 140px;
`;

/** A string_map leaf: label above, then one key/value row per entry. */
const MapWrap = styled.div`
  display: flex;
  flex-direction: column;
  gap: ${spacing['1']};
  min-width: 0;
  max-width: 520px;
`;

const MapRow = styled.div`
  display: grid;
  grid-template-columns: minmax(0, 1fr) minmax(0, 160px) 28px;
  align-items: center;
  gap: ${spacing['2']};

  /* The row's × and the add row's + share the last column, centred in it. */
  & > button:last-child {
    justify-self: center;
  }
`;

/** Keys like "D:Culture, Entertainment & Recreation" wrap rather than push the row wide. */
const MapKey = styled.span`
  font-size: ${typography.fontSize.xs};
  color: ${props => props.theme?.colors?.text?.primary || '#ffffff'};
  overflow-wrap: anywhere;
`;

const MapInput = styled.input`
  ${fieldSurface}
  width: 100%;
  min-width: 0;
`;

/** Sized to the row's last column: fieldSurface's padding would overflow it. */
const MapAdd = styled.button`
  ${fieldSurface}
  width: 28px;
  height: 28px;
  padding: 0;
  cursor: pointer;
`;

/** A value this portal cannot edit, shown exactly as stored. */
const ReadOnlyValue = styled.pre`
  margin: 0;
  max-width: 520px;
  max-height: 200px;
  overflow: auto;
  padding: ${spacing['1.5']} ${spacing['2']};
  font-family: ${typography.fontFamily.mono};
  font-size: ${typography.fontSize.xs};
  white-space: pre-wrap;
  overflow-wrap: anywhere;
  color: ${props => props.theme?.colors?.text?.secondary || '#94a3b8'};
  background: ${props => props.theme?.colors?.background?.secondary || 'rgba(26, 26, 26, 0.5)'};
  border: 1px solid ${props => props.theme?.colors?.border?.primary || 'rgba(255, 255, 255, 0.1)'};
  border-radius: ${borderRadius.md};
`;

// =============================================================================
// VALUE SHAPES
// =============================================================================

const EDITABLE_TYPES: ReadonlySet<string> = new Set([
  'boolean', 'integer', 'string', 'string[]', 'string_map',
]);

const isPlainObject = (v: unknown): v is Record<string, any> =>
  typeof v === 'object' && v !== null && !Array.isArray(v);

/**
 * Whether the stored value is exactly what the leaf's control writes: the
 * JavaScript type for a scalar, and every entry for a list or map. A mismatch
 * (an object under a `string` leaf, "5" under an `integer`, a nested object in
 * a map row) is shown read-only: the control would render it wrongly and the
 * first edit would overwrite the real value.
 */
const shapeFits = (type: string, v: unknown): boolean => {
  if (v === null || v === undefined) return true;
  switch (type) {
    // Every value too: a nested object in a row would render "[object Object]"
    // and the first keystroke would replace it, one level down.
    case 'string_map':
      return isPlainObject(v) && Object.values(v).every((x) => typeof x === 'string');
    case 'string[]':
      return Array.isArray(v) && v.every((x) => typeof x === 'string');
    case 'boolean':
      return typeof v === 'boolean';
    // A fraction would be truncated by the control's parseInt on the next edit.
    case 'integer':
      return Number.isInteger(v);
    case 'string':
      return typeof v === 'string';
    default:
      return false;
  }
};

// =============================================================================
// COMPONENT
// =============================================================================

const FlagLeafControl: React.FC<Props> = ({
  leaf, node, value, rolesVocabulary, disabled, onChange,
}) => {
  // Only the freeSolo string[] and string_map fields need local state, but a
  // hook cannot be conditional, so they are declared for every leaf.
  const [draft, setDraft] = useState('');
  const [mapKey, setMapKey] = useState('');
  const [mapValue, setMapValue] = useState('');
  const [mapNote, setMapNote] = useState('');

  const immutable = leaf.mutable === false;
  const isDisabled = !!(disabled || immutable);
  const effective = value === undefined ? leaf.default : value;
  const reason = leaf.immutable_reason || 'This value cannot be changed.';

  const selected: string[] = Array.isArray(effective) ? effective : [];

  const control = (() => {
    // Before every editable control: a type this portal does not know, or a
    // value that does not fit its type, must never reach a field that could
    // overwrite it. Leaving it untouched keeps it as stored on save.
    if (!EDITABLE_TYPES.has(leaf.type) || !shapeFits(leaf.type, effective)) {
      return (
        <LeafField $disabled as="div">
          <LeafKey>{leaf.key}</LeafKey>
          <ReadOnlyValue aria-label={leaf.key}>
            {effective === undefined || effective === null
              ? 'not set'
              : JSON.stringify(effective, null, 2)}
          </ReadOnlyValue>
          <LeafNote>
            This portal cannot edit a {EDITABLE_TYPES.has(leaf.type) ? `${leaf.type} leaf holding this value` : `${leaf.type} value`}.
            {value === undefined
              ? ' Nothing is stored for this tenant; GUS\'s default is shown.'
              : ' It is shown as stored and saving leaves it unchanged.'}
          </LeafNote>
        </LeafField>
      );
    }

    if (leaf.role === 'rbac') {
      return (
        <LeafField $disabled={isDisabled} as="div">
          <LeafKey>{leaf.key}</LeafKey>
          <ChipGroup role="group" aria-label={leaf.key}>
            {rolesVocabulary.map((role) => {
              const checked = selected.includes(role);
              return (
                <Chip key={role} $checked={checked} $disabled={isDisabled}>
                  <HiddenCheckbox
                    type="checkbox"
                    value={role}
                    checked={checked}
                    disabled={isDisabled}
                    // Same contract as the multi-select this replaces: always an
                    // array (empty means "all roles"), newly picked roles appended.
                    onChange={(e) => onChange(
                      e.target.checked
                        ? [...selected, role]
                        : selected.filter((r) => r !== role)
                    )}
                  />
                  {role}
                </Chip>
              );
            })}
            {selected.length === 0 && <LeafNote>all roles</LeafNote>}
          </ChipGroup>
        </LeafField>
      );
    }

    if (leaf.type === 'boolean') {
      return (
        <ToggleWrap $disabled={isDisabled}>
          <LeafToggle
            size="sm"
            disabled={isDisabled}
            label={leaf.key}
            // `!== false` is GUS's own rule (featureFlags.resolveEnabled): absent,
            // null and true all render the feature, so all three must show on.
            checked={effective !== false}
            onChange={(checked) => onChange(checked)}
          />
        </ToggleWrap>
      );
    }

    if (leaf.type === 'integer') {
      return (
        <LeafField $disabled={isDisabled}>
          <LeafKey>{leaf.key}</LeafKey>
          <NumberInput
            type="number"
            disabled={isDisabled}
            value={effective ?? ''}
            placeholder="default"
            onChange={(e) =>
              onChange(e.target.value === '' ? null : parseInt(e.target.value, 10))
            }
          />
        </LeafField>
      );
    }

    if (leaf.type === 'string[]') {
      // Without this, an array falls through to the text field below. Chips, not
      // comma-joined text: a controlled field re-split on every keystroke eats the
      // comma as it is typed. Non-rbac lists (Satisfaction's config.civic_domains)
      // land here; emptying one writes null.
      const commit = (raw: string) => {
        const entry = raw.trim();
        if (!entry || selected.includes(entry)) return;
        onChange([...selected, entry]);
      };
      return (
        <LeafField $disabled={isDisabled} as="div">
          <LeafKey>{leaf.key}</LeafKey>
          <ChipGroup>
            {selected.map((entry) => (
              <Chip key={entry} $checked $disabled={isDisabled} as="span">
                {entry}
                <ChipRemove
                  type="button"
                  disabled={isDisabled}
                  aria-label={`remove ${entry}`}
                  onClick={() => {
                    const next = selected.filter((v) => v !== entry);
                    onChange(next.length ? next : null);
                  }}
                >
                  ×
                </ChipRemove>
              </Chip>
            ))}
            <ChipInput
              type="text"
              disabled={isDisabled}
              value={draft}
              placeholder="type, then Enter"
              aria-label={leaf.key}
              onChange={(e) => setDraft(e.target.value)}
              onKeyDown={(e) => {
                if (e.key !== 'Enter') return;
                e.preventDefault();
                commit(draft);
                setDraft('');
              }}
              onBlur={() => {
                commit(draft);
                setDraft('');
              }}
            />
          </ChipGroup>
        </LeafField>
      );
    }

    if (leaf.type === 'string_map') {
      // Every edit reports the whole map, as every other leaf reports its whole
      // value. Emptying it reports null, which hands the key back to GUS's
      // default -- so null shows that default here too, as GUS will apply it.
      const usingDefault = !isPlainObject(value) && isPlainObject(leaf.default);
      const map: Record<string, string> = isPlainObject(value)
        ? value
        : isPlainObject(leaf.default) ? leaf.default : {};
      const report = (next: Record<string, string>) =>
        onChange(Object.keys(next).length ? next : null);
      // `explicit`: Enter or +, which explain a refusal. A blur adds a complete
      // pair silently (so a typed pair is not lost on save) and ignores a half one.
      const addEntry = (explicit: boolean) => {
        const k = mapKey.trim();
        const v = mapValue.trim();
        if (!k || !v) {
          if (explicit && (k || v)) setMapNote('Enter both a key and a value to add an entry.');
          return;
        }
        if (Object.prototype.hasOwnProperty.call(map, k)) {
          setMapNote(`"${k}" is already set; edit its row instead.`);
          return;
        }
        report({ ...map, [k]: v });
        setMapKey('');
        setMapValue('');
        setMapNote('');
      };
      const addOnEnter = (e: React.KeyboardEvent) => {
        if (e.key !== 'Enter') return;
        e.preventDefault();
        addEntry(true);
      };
      // A value is tidied when its row loses focus, not per keystroke, so it
      // can be cleared and retyped. Left blank, the row is removed: an empty
      // value is never a meaningful setting.
      const tidyRow = (k: string) => {
        const v = (map[k] ?? '').trim();
        if (v === map[k]) return;
        if (v) {
          report({ ...map, [k]: v });
        } else {
          const { [k]: _removed, ...rest } = map;
          report(rest);
        }
      };
      return (
        <MapWrap role="group" aria-label={leaf.key}>
          <LeafKey>{leaf.key}</LeafKey>
          {usingDefault && <LeafNote>Not set for this tenant; GUS&apos;s default is shown.</LeafNote>}
          {Object.entries(map).map(([k, v]) => (
            <MapRow key={k}>
              <MapKey>{k}</MapKey>
              <MapInput
                type="text"
                disabled={isDisabled}
                value={v}
                aria-label={`${leaf.key} ${k}`}
                onChange={(e) => report({ ...map, [k]: e.target.value })}
                onBlur={() => tidyRow(k)}
              />
              <ChipRemove
                type="button"
                disabled={isDisabled}
                aria-label={`remove ${k}`}
                onClick={() => {
                  const { [k]: _removed, ...rest } = map;
                  report(rest);
                }}
              >
                ×
              </ChipRemove>
            </MapRow>
          ))}
          <MapRow>
            <MapInput
              type="text"
              disabled={isDisabled}
              value={mapKey}
              placeholder="key"
              aria-label={`${leaf.key} new key`}
              onChange={(e) => { setMapKey(e.target.value); setMapNote(''); }}
              onKeyDown={addOnEnter}
              onBlur={() => addEntry(false)}
            />
            <MapInput
              type="text"
              disabled={isDisabled}
              value={mapValue}
              placeholder="value, then Enter"
              aria-label={`${leaf.key} new value`}
              onChange={(e) => { setMapValue(e.target.value); setMapNote(''); }}
              onKeyDown={addOnEnter}
              onBlur={() => addEntry(false)}
            />
            <MapAdd
              type="button"
              disabled={isDisabled}
              aria-label={`add to ${leaf.key}`}
              onClick={() => addEntry(true)}
            >
              +
            </MapAdd>
          </MapRow>
          {mapNote && <LeafNote role="status">{mapNote}</LeafNote>}
        </MapWrap>
      );
    }

    // string, including the caption leaves
    return (
      <LeafField $disabled={isDisabled}>
        <LeafKey>{leaf.key}</LeafKey>
        <TextInput
          type="text"
          disabled={isDisabled}
          value={effective ?? ''}
          placeholder={leaf.role === 'caption_ar' ? node.label_ar ?? '' : node.label}
          dir={leaf.role === 'caption_ar' ? 'rtl' : undefined}
          onChange={(e) => onChange(e.target.value === '' ? null : e.target.value)}
        />
      </LeafField>
    );
  })();

  return (
    <LeafWrap title={immutable ? reason : undefined}>
      {control}
      {/* Surfaced, not hidden behind a hover: a reason only a mouse can reach
          is no reason at all on a touch screen, and this one explains why the
          control next to it will not move. */}
      {immutable && <LeafNote>{reason}</LeafNote>}
    </LeafWrap>
  );
};

export default FlagLeafControl;
